import { useEffect, useState } from 'react'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface CounterProps {
  value: string
  durationMs?: number
}

const parse = (value: string): { target: number; prefix: string; suffix: string } | null => {
  const match = /^(\D*)(\d+)(\D*)$/.exec(value)
  if (match === null) return null
  const [, prefix = '', digits = '', suffix = ''] = match
  const target = Number.parseInt(digits, 10)
  return Number.isFinite(target) ? { target, prefix, suffix } : null
}

const easeOutExpo = (t: number): number => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t))

export function Counter({ value, durationMs = 1600 }: CounterProps) {
  const parsed = parse(value)
  const target = parsed?.target
  const reduced = useReducedMotion()
  const [ref, inView] = useInView<HTMLSpanElement>({ threshold: 0.4 })
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (target === undefined || !inView || reduced) return

    let frame = 0
    const start = performance.now()
    const step = (now: number) => {
      const progress = Math.min((now - start) / durationMs, 1)
      setCurrent(Math.round(easeOutExpo(progress) * target))
      if (progress < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)

    return () => {
      cancelAnimationFrame(frame)
    }
  }, [inView, reduced, durationMs, target])

  if (parsed === null) {
    return <span ref={ref}>{value}</span>
  }

  const shown = reduced || !inView ? parsed.target : current

  return (
    <span ref={ref}>
      {parsed.prefix}
      {shown}
      {parsed.suffix}
    </span>
  )
}
